import type { ListingsRequestBuilder } from '@noths/polaris-client-listings-api';

import { getDeliveryDate, getDeliveryDaysFromValue } from './deliveryChipDates';

export interface GuaranteedArrivalDayConfig {
  applyToRequest: (builder: ListingsRequestBuilder) => void;
  /** The day as a shopper reads it, "Saturday". */
  name: string;
  /** The `guaranteed` query param value. */
  urlValue: string;
  /** As `Date.getUTCDay` counts, Sunday is 0. */
  weekday: number;
}

/**
 * Days a delivery method guarantees arrival on, as opposed to the estimated "on or before"
 * every other date means. A guaranteed day is a condition on a chosen date rather than a
 * facet option: options on one facet OR together, so as an option it would widen the result
 * set instead of narrowing it.
 */
export const GUARANTEED_ARRIVAL_DAYS = {
  SATURDAY: {
    applyToRequest: (builder) => {
      builder.saturdayArrival(true);
    },
    name: 'Saturday',
    urlValue: 'saturday',
    weekday: 6,
  },
} satisfies Record<string, GuaranteedArrivalDayConfig>;

export type GuaranteedArrivalDay = keyof typeof GUARANTEED_ARRIVAL_DAYS;

// The rules take the config as a parameter so a spec can prove they hold for a day not yet
// configured. Callers leave it out.
type GuaranteedArrivalDays<Day extends string> = Record<Day, GuaranteedArrivalDayConfig>;

const defaultDays = <Day extends string>() =>
  GUARANTEED_ARRIVAL_DAYS as unknown as GuaranteedArrivalDays<Day>;

const findDay = <Day extends string>(
  days: GuaranteedArrivalDays<Day>,
  matches: (config: GuaranteedArrivalDayConfig) => boolean,
): Day | undefined => (Object.keys(days) as Day[]).find((day) => matches(days[day]));

/** The guaranteed day that a lead time of `days` from `from` lands on, if it lands on one. */
export const getGuaranteedArrivalDayForDays = <Day extends string = GuaranteedArrivalDay>(
  days: number,
  from: Date = new Date(),
  guaranteedDays: GuaranteedArrivalDays<Day> = defaultDays<Day>(),
): Day | undefined => {
  const weekday = getDeliveryDate(days, from).getUTCDay();

  return findDay(guaranteedDays, (config) => config.weekday === weekday);
};

/**
 * The requested day if the chosen delivery date is that day, and otherwise nothing. Every
 * place the condition is held, linked to or sent goes through this, so choosing another
 * date clears it rather than leaving it set but hidden: the API would ignore it, and a
 * shared URL would carry it.
 */
export const resolveGuaranteedArrivalDay = <Day extends string = GuaranteedArrivalDay>(
  requested: Day | undefined,
  deliveryDaysValue: string | undefined,
  from: Date = new Date(),
  guaranteedDays: GuaranteedArrivalDays<Day> = defaultDays<Day>(),
): Day | undefined => {
  const days =
    deliveryDaysValue === undefined ? undefined : getDeliveryDaysFromValue(deliveryDaysValue);

  if (!requested || days === undefined) {
    return undefined;
  }

  return getGuaranteedArrivalDayForDays(days, from, guaranteedDays) === requested
    ? requested
    : undefined;
};

export const parseGuaranteedArrivalDay = <Day extends string = GuaranteedArrivalDay>(
  urlValue: string | undefined,
  guaranteedDays: GuaranteedArrivalDays<Day> = defaultDays<Day>(),
): Day | undefined =>
  urlValue === undefined
    ? undefined
    : findDay(guaranteedDays, (config) => config.urlValue === urlValue);
